import { agentNetworkService } from '../agentNetwork/agentNetworkService';
import { securityService } from '../security/securityService';
import { filterBySurface } from '../../pages/agent-network/utils/filterBySurface';
import { CUSTOM_AGENTS_MODULE } from '../../pages/custom-agents/customAgentsModule';
import { readOrEmpty } from '../security/surfaceScope';
import { sourceMetadata } from '../../utils/sourceDisplayName';

export { sourceMetadata };

const endpointId = (endpoint) => (
  typeof endpoint === 'object' ? endpoint?.id : endpoint
);

export const timeRangeFromParams = (params = {}) => {
  const start = Date.parse(params.start_time || '');
  const end = Date.parse(params.end_time || '');
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return undefined;
  const days = Math.max(1, Math.ceil((end - start) / 86400000));
  if (days <= 1) return 'last_24_hours';
  if (days <= 7) return 'last_7_days';
  if (days <= 30) return 'last_30_days';
  return 'last_90_days';
};

/**
 * Graph topology -> the directory contract used by Source Intelligence: every datasource
 * node, with the agents that reached it. Atlas reads the whole estate this way; Custom
 * Agents first scopes it to its surface (mapCustomSourceTopology).
 */
export const mapSourceTopology = (topology = {}) => {
  const nodes = topology?.nodes || [];
  const links = topology?.links || [];
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const connected = new Map();

  links.forEach((link) => {
    const source = endpointId(link.source);
    const target = endpointId(link.target);
    if (!connected.has(source)) connected.set(source, []);
    if (!connected.has(target)) connected.set(target, []);
    connected.get(source).push({ id: target, count: link.count || 0, lastSeen: link.last_seen });
    connected.get(target).push({ id: source, count: link.count || 0, lastSeen: link.last_seen });
  });

  const sources = nodes.filter((node) => node.type === 'datasource').map((node) => {
    const metadata = sourceMetadata(node.label);
    const neighbours = connected.get(node.id) || [];
    const agents = neighbours
      .map(({ id }) => nodeById.get(id))
      .filter((peer) => peer && (peer.type === 'agent' || peer.type === 'subagent'));
    const interactionCount = neighbours.reduce((sum, edge) => sum + edge.count, 0);
    const lastObserved = neighbours
      .map((edge) => edge.lastSeen)
      .filter((value) => Number.isFinite(Date.parse(value)))
      .sort((left, right) => Date.parse(right) - Date.parse(left))[0]
      || node.last_seen
      || null;

    return {
      id: node.id,
      title: node.label || node.id,
      uri: metadata.uri || node.id,
      domain: metadata.domain,
      source_type: metadata.sourceType,
      is_external: metadata.isExternal,
      // Custom telemetry records source access, but not grounding citations.
      citation_count: 0,
      interaction_count: interactionCount || node.call_count || node.degree || 0,
      last_observed: lastObserved,
      agent_names: [...new Set(agents.map((agent) => agent.label).filter(Boolean))],
      // The agents' stable identifiers (a display name can be the registry's), so a link that
      // names one agent (`?agent=`) finds its sources.
      agent_ids: [...new Set(agents.map((agent) => agent.agent_identifier || agent.agent_id).filter(Boolean))],
      last_cited: null,
    };
  });

  return {
    sources,
    total: sources.length,
    summary: {
      total_sources: sources.length,
      internal_sources: sources.filter((source) => !source.is_external).length,
      external_sources: sources.filter((source) => source.is_external).length,
      total_citations: 0,
    },
  };
};

/** Custom graph topology -> the Source Intelligence directory, scoped to the custom surface. */
export const mapCustomSourceTopology = (topology = {}) => (
  mapSourceTopology(filterBySurface(topology, [CUSTOM_AGENTS_MODULE.graphSurface]))
);

const findingTypeFor = (category = '') => {
  const normalized = category.toLowerCase();
  if (normalized.includes('access')) return 'overprivileged_agent';
  if (normalized.includes('data protection')) return 'data_store_sensitivity';
  return 'instruction_risk';
};

/** Custom security-posture breakdown -> the Gemini-style Access Management contract. */
export const mapCustomPostureBreakdown = (breakdown = {}) => {
  const score = breakdown.posture_score || {};
  const scanTimestamp = score.last_updated || null;
  const scanId = scanTimestamp ? `custom-posture-${scanTimestamp}` : 'custom-posture-current';
  const findings = (breakdown.risks || []).flatMap((risk) => {
    const agents = risk.affected_agents?.length ? risk.affected_agents : [''];
    const findingType = findingTypeFor(risk.category);
    return agents.map((agent, index) => ({
      id: agents.length > 1 ? `${risk.id}-${index + 1}` : risk.id,
      scan_id: scanId,
      scan_timestamp: scanTimestamp,
      finding_type: findingType,
      severity: risk.risk_level || 'medium',
      status: risk.status || 'open',
      description: risk.description || risk.title || '',
      recommendation: risk.remediation || '',
      agent_name: agent,
      roles: [],
      permissions: [],
      data_store_ids: [],
      sensitivity_level: findingType === 'data_store_sensitivity' ? 'sensitive' : undefined,
      details: {
        category: risk.category || '',
        risk_title: risk.title || '',
        posture_score: score.overall_score,
        sensitivity_level: findingType === 'data_store_sensitivity' ? 'sensitive' : undefined,
      },
    }));
  });

  const count = (types) => findings.filter((finding) => types.has(finding.finding_type)).length;
  return {
    findings,
    metrics: {
      enabled: true,
      running: false,
      total_scans: scanTimestamp ? 1 : 0,
      total_findings: findings.length,
      total_iam_findings: count(new Set(['overprivileged_agent', 'overprivileged_sa', 'impersonation_chain'])),
      total_drift_findings: count(new Set([
        'new_agent', 'deleted_agent', 'model_changed', 'instruction_changed',
        'data_store_changed', 'sub_agent_changed', 'tools_changed', 'name_changed',
      ])),
      total_shadow_access_findings: count(new Set(['shadow_access'])),
      last_scan_at: scanTimestamp,
      errors: 0,
    },
  };
};

let postureRequest = null;

const loadPosture = () => {
  if (!postureRequest) {
    postureRequest = readOrEmpty(
      () => securityService.securityPosture.getFullBreakdown(),
      { posture_score: {}, risks: [] },
    )
      .then(mapCustomPostureBreakdown)
      .finally(() => { postureRequest = null; });
  }
  return postureRequest;
};

export const customAgentsSourceAdapter = {
  async getSources(params = {}) {
    // The custom graph surface, explicitly: without it the request inherits the
    // module's pinned security surface (`otel`), which no graph node carries.
    const topology = await readOrEmpty(
      () => agentNetworkService.getTopology({
        surface: CUSTOM_AGENTS_MODULE.graphSurface,
        timeRange: timeRangeFromParams(params),
      }),
      { nodes: [], links: [] },
    );
    return mapCustomSourceTopology(topology);
  },
};

export const customAgentsAccessAdapter = {
  getPostureMetrics: async () => (await loadPosture()).metrics,
  getPostureFindings: async () => {
    const posture = await loadPosture();
    return { findings: posture.findings, total: posture.findings.length };
  },
};
